import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getLogoConfig, getPublicStaticPages } from '../../lib/api';
import { defaultLogoState, withResolvedLogo } from '../../lib/branding';
import { ChevronDown, Mail, Phone, MapPin, Menu, X } from 'lucide-react';

const MARKETING_LINKS = [
  { to: '/package-forwarding', label: 'Package Forwarding' },
  { to: '/personal-shopper', label: 'Personal Shopper' },
  { to: '/fulfillment', label: 'Fulfillment' },
];

const SUPPORT_LINKS = [
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/shipping-calculator', label: 'Calculator' },
  { to: '/faq', label: 'FAQ' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

const COUNTRY_LINKS = [
  { to: '/shop-from-india-usa', label: 'Ship to USA', flag: '🇺🇸' },
  { to: '/shop-from-india-canada', label: 'Ship to Canada', flag: '🇨🇦' },
  { to: '/shop-from-india-uk', label: 'Ship to UK', flag: '🇬🇧' },
  { to: '/shop-from-india-australia', label: 'Ship to Australia', flag: '🇦🇺' },
  { to: '/shop-from-india-uae', label: 'Ship to UAE', flag: '🇦🇪' },
];

export function MarketingHeader({ logo }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const servicesRef = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#FCFBF8]/85 backdrop-blur-xl border-b border-black/5 supports-[backdrop-filter]:bg-[#FCFBF8]/70" data-testid="marketing-header">
      <div className="max-w-7xl 2xl:max-w-[1500px] mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="marketing-header-logo">
          {logo?.logo_url ? (
            <img src={logo.logo_url} alt="ShipDesi" style={{ height: `${Math.min(Number(logo.header_height) || 96, 100)}px` }} className="w-auto rounded-2xl" />
          ) : (
            <span className="text-xl font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>ShipDesi</span>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <div className="relative" ref={servicesRef}>
            <button onClick={() => setServicesOpen(o => !o)}
              className="flex items-center gap-1 text-sm font-semibold text-[#4A4D54] hover:text-[#1A1B1E] transition-colors"
              data-testid="marketing-nav-services">
              Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-white rounded-2xl border border-[#EBE8E4] shadow-xl p-2" data-testid="marketing-services-dropdown">
                {MARKETING_LINKS.map(l => (
                  <Link key={l.to} to={l.to} onClick={() => setServicesOpen(false)}
                    className="block px-4 py-2.5 rounded-xl text-sm font-semibold text-[#2C2825] hover:bg-[#F6F5F2] transition-colors"
                    data-testid={`marketing-services-${l.to.replace('/', '')}`}>
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {SUPPORT_LINKS.map(l => (
            <Link key={l.to} to={l.to}
              className="text-sm font-semibold text-[#4A4D54] hover:text-[#1A1B1E] transition-colors"
              data-testid={`marketing-nav-${l.to.replace('/', '')}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="marketing-header-dashboard"
                className="hidden sm:inline-block bg-[#1A1B1E] text-white px-5 py-2.5 rounded-full text-sm font-bold hover:-translate-y-0.5 hover:shadow-lg transition-all">
                Dashboard
              </Link>
              <button onClick={logout} data-testid="marketing-header-logout"
                className="hidden sm:inline-block border border-[#EBE8E4] text-[#4A4D54] px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-[#F6F5F2] transition-all">
                Logout
              </button>
            </>
          ) : (
            <button onClick={() => navigate('/login')} data-testid="marketing-header-signin"
              className="bg-[#1A1B1E] text-white px-5 py-2.5 rounded-full text-sm font-bold hover:-translate-y-0.5 hover:shadow-lg transition-all">
              Sign In
            </button>
          )}
          <button onClick={() => setMobileOpen(o => !o)} className="lg:hidden p-2 rounded-lg hover:bg-[#F6F5F2]" data-testid="marketing-mobile-toggle">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#EBE8E4] bg-[#FCFBF8]/95 backdrop-blur-xl supports-[backdrop-filter]:bg-[#FCFBF8]/85 px-6 py-4" data-testid="marketing-mobile-menu">
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#8F8984] mb-2">Services</p>
          <div className="space-y-1 mb-4">
            {MARKETING_LINKS.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                className="block py-2 text-sm font-semibold text-[#2C2825]">{l.label}</Link>
            ))}
          </div>
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#8F8984] mb-2">Learn</p>
          <div className="space-y-1 mb-4">
            {SUPPORT_LINKS.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                className="block py-2 text-sm font-semibold text-[#2C2825]">{l.label}</Link>
            ))}
          </div>
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#8F8984] mb-2">Ship To</p>
          <div className="space-y-1 mb-4">
            {COUNTRY_LINKS.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                className="block py-2 text-sm font-semibold text-[#2C2825]"
                data-testid={`marketing-mobile-country-${l.to.replace(/\//g, '')}`}>
                {l.flag} {l.label}
              </Link>
            ))}
          </div>
          {user ? (
            <div className="pt-2 border-t border-[#EBE8E4] space-y-2">
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} data-testid="marketing-mobile-dashboard"
                className="block w-full text-center bg-[#1A1B1E] text-white px-5 py-2.5 rounded-full text-sm font-bold">
                Dashboard
              </Link>
              <button type="button" onClick={() => { setMobileOpen(false); logout(); }} data-testid="marketing-mobile-logout"
                className="block w-full border border-[#EBE8E4] text-[#4A4D54] px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-[#F6F5F2] transition-all">
                Logout
              </button>
            </div>
          ) : (
            <div className="pt-2 border-t border-[#EBE8E4]">
              <button type="button" onClick={() => { setMobileOpen(false); navigate('/login'); }} data-testid="marketing-mobile-signin"
                className="block w-full bg-[#1A1B1E] text-white px-5 py-2.5 rounded-full text-sm font-bold">
                Sign In
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

function MarketingFooter({ logo, legalPages }) {
  return (
    <footer className="bg-[#1A1B1E] text-white py-16 px-6" data-testid="marketing-footer">
      <div className="max-w-7xl 2xl:max-w-[1500px] mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10 mb-12">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4" data-testid="marketing-footer-logo">
              {logo?.logo_url ? (
                <img src={logo.logo_url} alt="ShipDesi" style={{ height: `${logo.footer_height || 50}px` }} className="w-auto brightness-0 invert" />
              ) : (
                <span className="text-lg font-black tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>ShipDesi</span>
              )}
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed max-w-sm">
              India's premier parcel forwarding, personal shopper and fulfillment service. Connecting India with the world, one package at a time.
            </p>
            <p className="text-xs text-gray-500 mt-4 leading-relaxed max-w-sm" data-testid="marketing-footer-brand-owner">
              ShipDesi is a brand owned and operated by <span className="text-gray-300 font-semibold">AG Renai &amp; Co.</span>
            </p>
          </div>
          <div>
            <h4 className="font-bold text-sm mb-4">Services</h4>
            <div className="space-y-2">
              {MARKETING_LINKS.map(l => (
                <Link key={l.to} to={l.to} className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid={`marketing-footer-${l.to.replace('/', '')}`}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-bold text-sm mb-4">Learn</h4>
            <div className="space-y-2">
              {SUPPORT_LINKS.map(l => (
                <Link key={l.to} to={l.to} className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid={`marketing-footer-link-${l.to.replace('/', '')}`}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-bold text-sm mb-4">Ship To</h4>
            <div className="space-y-2">
              {COUNTRY_LINKS.map(l => (
                <Link key={l.to} to={l.to} className="block text-sm text-gray-400 hover:text-white transition-colors" data-testid={`marketing-footer-country-${l.to.split('-').pop()}`}>
                  <span className="mr-1.5">{l.flag}</span>{l.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-bold text-sm mb-4">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-400"><Mail className="w-4 h-4" /> support@shipdesi.com</div>
              <div className="text-sm text-gray-400 leading-relaxed" data-testid="marketing-footer-address">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-300">AG Renai &amp; Co</p>
                    <p>15/773 Karanjikudy</p>
                    <p>Perumbavoor, Kerala</p>
                    <p>India</p>
                  </div>
                </div>
              </div>
            </div>
            {legalPages && legalPages.length > 0 && (
              <div className="mt-5 pt-4 border-t border-gray-800 space-y-2">
                {legalPages.map(p => (
                  <Link key={p.slug} to={`/legal/${p.slug}`} className="block text-[11px] text-gray-500 hover:text-white" data-testid={`marketing-footer-legal-${p.slug}`}>
                    {p.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} AG Renai &amp; Co. All rights reserved. ShipDesi is a registered brand of AG Renai &amp; Co.
        </div>
      </div>
    </footer>
  );
}

export function MarketingLayout({ children }) {
  const [logo, setLogo] = useState(() => {
    try {
      return withResolvedLogo(JSON.parse(localStorage.getItem('shipdesi_logo') || 'null') || defaultLogoState());
    } catch {
      return defaultLogoState();
    }
  });
  const [legalPages, setLegalPages] = useState([]);

  useEffect(() => {
    getLogoConfig().then(r => {
      if (r.data) {
        const resolved = withResolvedLogo(r.data);
        setLogo(resolved);
        try { localStorage.setItem('shipdesi_logo', JSON.stringify(resolved)); } catch { /* ignore */ }
      }
    }).catch(() => {});
    getPublicStaticPages().then(r => setLegalPages(r.data.pages || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col">
      <MarketingHeader logo={logo} />
      <main className="flex-1 pt-[88px]">{children}</main>
      <MarketingFooter logo={logo} legalPages={legalPages} />
    </div>
  );
}
